import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";
import { Footer } from "../components/Footer";

const CONTACT = (
  <div data-testid="legal-contact-block" className="mt-14 border-t border-border pt-6 text-xs leading-relaxed text-sand/70">
    <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-cyanx uppercase">Contact us</p>
    <p className="mt-2">
      Off-Road Electric Bikes — trading name of Eco-Bikes &amp; Camping Supplies Co., Anthony Bain (sole trader)
      <br />
      Registered address: 20-23 Woodside Place, Glasgow, G3 7QL
      <br />
      Email: ecobikecampsupp@gmail.com · Phone: 07522 364950 · offroadelectricbikes.co.uk
    </p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="font-serif text-xl font-black uppercase tracking-tight text-bone">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-sand">{children}</div>
  </div>
);

const LegalLayout = ({ label, title, testid, children }) => (
  <div data-testid={testid} className="min-h-screen bg-forest-deep text-bone">
    <div className="grain-overlay" />
    <header className="border-b border-border bg-forest-deep/85">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/" data-testid="legal-logo-link" className="flex items-center gap-2.5">
          <span className="clip-corner-sm flex h-9 w-9 items-center justify-center bg-brass">
            <Zap className="h-5 w-5 text-white" strokeWidth={2.25} fill="currentColor" />
          </span>
          <span className="font-serif text-lg font-black uppercase leading-none tracking-wide text-bone">
            Off-Road Electric
            <span className="block font-mono text-[9px] font-medium tracking-[0.42em] text-cyanx">Bikes</span>
          </span>
        </Link>
        <Link
          to="/"
          data-testid="legal-back-link"
          className="flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.2em] text-sand uppercase transition-colors hover:text-brass"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to store
        </Link>
      </div>
    </header>
    <main className="mx-auto max-w-3xl px-5 py-16 lg:py-20">
      <p className="mono-label flex items-center gap-3">
        <span className="inline-block h-0.5 w-10 bg-brass" />
        {label}
      </p>
      <h1 className="mt-3 font-serif text-4xl font-black uppercase tracking-tight text-bone sm:text-5xl">
        {title}
      </h1>
      <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-sand/50 uppercase">
        Last updated: October 2026
      </p>
      {children}
      {CONTACT}
    </main>
    <Footer />
  </div>
);

export const TermsPage = () => (
  <LegalLayout label="Legal" title="Terms & Conditions" testid="terms-page">
    <Section title="1. About us">
      <p>
        This website, offroadelectricbikes.co.uk, is operated by Off-Road
        Electric Bikes — the trading name of Eco-Bikes &amp; Camping Supplies Co.,
        a sole trader business run by Anthony Bain. Registered address: 20-23
        Woodside Place, Glasgow, G3 7QL. You can reach us at
        ecobikecampsupp@gmail.com or 07522 364950.
      </p>
      <p>
        By placing an order or submitting an enquiry through this website, you
        agree to these Terms &amp; Conditions.
      </p>
    </Section>

    <Section title="2. Orders & fulfilment">
      <p>
        Orders are fulfilled by our suppliers and shipped directly to you. We do
        not hold or ship stock ourselves. When you place an order, we pass your
        order and delivery details to the relevant supplier, who dispatches the
        goods to your door.
      </p>
      <p>
        Many of our bikes are sold on a pre-order basis. Lead times shown on each
        listing (for example 1–2 weeks or 5–10 working days) are estimates
        provided in good faith and may change due to supplier availability or
        shipping delays. We will always keep you informed if a lead time changes.
      </p>
      <p>
        All sales are online only. We do not offer test rides, trade-ins or
        showroom viewings. Every bike ships direct from the supplier to your door
        at one all-in delivered price.
      </p>
    </Section>

    <Section title="3. Pricing & payment">
      <p>
        All prices shown are the full, all-inclusive price in pounds sterling —
        UK delivery is included. There are no hidden costs and no separate
        shipping charges added later. Where a listing says "confirm on enquiry",
        we will confirm the current all-in price before you commit to anything.
      </p>
      <p>
        Payment is taken in full at the point of order via our payment processor,
        Stripe. An order is only confirmed once payment has been received.
      </p>
    </Section>

    <Section title="4. Cancellations">
      <p>
        If you cancel before your order has been dispatched, we will refund you
        in full within 5 working days. After delivery, your rights are set out in
        our Returns &amp; Refund Policy, including your 14-day right to cancel
        under the Consumer Contracts Regulations 2013.
      </p>
    </Section>

    <Section title="5. Off-road bikes — private land use only">
      <p>
        Off-road bikes sold on this website — including Sur-Ron, Talaria and OSET
        models — are NOT road legal. They are for use on private land only, with
        the landowner's permission. The rider assumes full responsibility for
        safe and lawful use on appropriate land, and for complying with all
        applicable laws.
      </p>
    </Section>

    <Section title="6. Road-legal bikes">
      <p>
        Road-legal bikes sold on this website — including the HitWay BK-U1 and
        HitWay BK15 — are EN 15194 certified and comply with UK Electrically
        Assisted Pedal Cycle (EAPC) regulations: a motor rated at no more than
        250W, a maximum assisted speed of 25 km/h, and power that only engages
        while pedalling. No licence, tax or insurance is required to ride them on
        UK public roads, but you must follow the Highway Code.
      </p>
    </Section>

    <Section title="7. Modifications">
      <p>
        Modifying any bike in a way that makes it non-compliant with its
        certification — for example, removing or defeating the speed limiter on a
        road-legal bike — is illegal. Any such modification voids all liability
        on our part, and the rider assumes full legal responsibility for the
        modified bike and its use.
      </p>
    </Section>

    <Section title="8. Limitation of liability">
      <p>
        Nothing in these terms limits our liability for death or personal injury
        caused by our negligence, for fraud, or for any liability that cannot be
        excluded by law. Subject to that, our total liability to you in
        connection with any order is limited to the price you paid for the goods
        concerned. We are not liable for any indirect or consequential loss, or
        for loss caused by misuse, modification, or use of a bike contrary to
        these terms.
      </p>
    </Section>

    <Section title="9. Governing law">
      <p>
        These terms are governed by the laws of England and Wales, and any
        dispute will be subject to the jurisdiction of the courts of England and
        Wales.
      </p>
    </Section>
  </LegalLayout>
);

export const PrivacyPage = () => (
  <LegalLayout label="Legal" title="Privacy Policy" testid="privacy-page">
    <Section title="1. Who we are">
      <p>
        Off-Road Electric Bikes — the trading name of Eco-Bikes &amp; Camping
        Supplies Co. (Anthony Bain, sole trader) — is the data controller for the
        personal data collected through offroadelectricbikes.co.uk. Registered
        address: 20-23 Woodside Place, Glasgow, G3 7QL. Contact:
        ecobikecampsupp@gmail.com · 07522 364950.
      </p>
    </Section>

    <Section title="2. What we collect">
      <p>
        We collect only what we need to handle your enquiry or order:
      </p>
      <p>
        Name, email address, phone number, and delivery address. Payment details
        are handled directly by Stripe — we never see or store your card number.
      </p>
    </Section>

    <Section title="3. How we use it">
      <p>
        Your data is used to process enquiries and orders, coordinate delivery
        with our suppliers, and contact you about your order. We do not sell your
        personal data, and we do not share it with anyone for marketing purposes.
      </p>
    </Section>

    <Section title="4. Who we share it with">
      <p>
        We share your data only where necessary to fulfil your order:
      </p>
      <p>
        Stripe (payment processing) and our suppliers — currently including
        HitWay — who need your name, contact details and delivery address to ship
        your order directly to you. Each of these parties processes your data
        under its own privacy policy and data protection obligations.
      </p>
    </Section>

    <Section title="5. How long we keep it">
      <p>
        Order records are kept for 7 years, as required by HMRC for tax purposes.
        Enquiry data that does not lead to an order is deleted after 12 months.
      </p>
    </Section>

    <Section title="6. Cookies">
      <p>
        This website uses only essential session cookies needed for it to
        function (for example, keeping the admin console signed in). We do not
        use advertising, analytics or tracking cookies.
      </p>
    </Section>

    <Section title="7. Your rights">
      <p>
        Under UK GDPR you have the right to access the personal data we hold
        about you, to have it corrected, and to have it erased (subject to our
        legal obligation to retain order records for 7 years). To exercise any of
        these rights, email ecobikecampsupp@gmail.com and we will respond within
        30 days.
      </p>
      <p>
        If you are unhappy with how we handle your data, you can complain to the
        Information Commissioner's Office (ICO) at ico.org.uk.
      </p>
    </Section>
  </LegalLayout>
);

export const ReturnsPage = () => (
  <LegalLayout label="Legal" title="Returns & Refund Policy" testid="returns-page">
    <Section title="1. 14-day right to cancel">
      <p>
        Under the Consumer Contracts Regulations 2013, you have 14 days from the
        date of delivery to cancel your order for any reason. The bike must be
        unused and in its original condition and packaging. You pay the return
        postage unless the item is faulty or not as described.
      </p>
    </Section>

    <Section title="2. Faulty or not-as-described goods">
      <p>
        Under the Consumer Rights Act 2015, if your bike is faulty or not as
        described you are entitled to a full refund within 30 days of delivery.
        After 30 days and up to 6 months, we will arrange a repair or
        replacement; if that is not possible, you are entitled to a refund.
        Return postage for faulty goods is covered by us.
      </p>
    </Section>

    <Section title="3. How to start a return">
      <p>
        Email ecobikecampsupp@gmail.com with your order details (name, order
        date, bike model). Because orders are fulfilled by our suppliers, we will
        coordinate with the relevant supplier to arrange collection or a return
        label, and confirm the next steps by email. Do not send a bike back
        before we confirm the return arrangements. Return arrangements for bikes
        are agreed case by case with the supplier; we will always confirm the
        process in writing before you send anything back.
      </p>
    </Section>

    <Section title="4. What is not covered">
      <p>
        Bikes damaged through misuse, modification, or accident — including use
        or accident on private land — are not covered by this policy. Normal wear
        and tear is not a fault.
      </p>
    </Section>

    <Section title="5. Cancelling before dispatch">
      <p>
        If you cancel before your order has been dispatched, we will refund you
        in full within 5 working days of your cancellation.
      </p>
    </Section>
  </LegalLayout>
);
